import React, { useState, useEffect, useRef } from 'react';
import { UserButton, useUser, OrganizationSwitcher, useOrganization, useClerk } from '@clerk/clerk-react';
import { GitBranch } from 'lucide-react';
import { getApiBaseUrl, getOrgSlug } from '../config/backend.config';
import ThemeToggle from './ThemeToggle';

const branchName = process.env.REACT_APP_BRANCH_NAME;

function Header({ isConnected, onLogout, headerHidden }) {
  const { user } = useUser();
  const clerk = useClerk();
  const { organization } = useOrganization();
  const [versionInfo, setVersionInfo] = useState(null);
  const organizationRef = useRef(organization);
  const clerkRef = useRef(clerk);

  useEffect(() => {
    organizationRef.current = organization;
    clerkRef.current = clerk;
  }, [organization, clerk]);

  // Fetch version information
  useEffect(() => {
    const fetchData = async () => {
      try {
        if (!organization?.id) {
          return;
        }

        const orgSlug = getOrgSlug(organizationRef.current);
        const apiBaseUrl = getApiBaseUrl(orgSlug);

        if (!clerkRef.current.session) {
          console.error('Clerk session not available');
          return;
        }

        const sessionToken = await clerkRef.current.session.getToken();
        if (!sessionToken) {
          console.error('Unable to get authentication token from session');
          return;
        }

        const headers = {
          'Authorization': `Bearer ${sessionToken}`,
          'Content-Type': 'application/json',
        };

        // Fetch version info
        const versionResponse = await fetch(`${apiBaseUrl}/version`, { headers });
        if (versionResponse.ok) {
          const data = await versionResponse.json();
          setVersionInfo(data);
        }
      } catch (error) {
        console.error('Failed to fetch version info:', error);
      }
    };

    fetchData();
  }, [organization?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <header className={`header${headerHidden ? ' header-hidden' : ''}`}>
      <div className="header-logo">
        <img
          src="/logo.png"
          alt="Kwartz Logo"
          onError={(e) => { e.target.src = '/logo.png'; }}
        />
        <h1>Kwartz Analytics</h1>
        {versionInfo && (
          <span className="version-subtle" title={`Build: ${versionInfo.build_number || 'N/A'}\nDate: ${versionInfo.build_date || 'N/A'}`}>
            v{versionInfo.version}
          </span>
        )}
        {branchName && (
          <span className="branch-indicator" title={`Branch: ${branchName}`}>
            <GitBranch size={12} />
            {branchName}
          </span>
        )}
      </div>

      <div className="header-info">
        <ThemeToggle />

        {user && (
          <>
            {/* Organization Switcher - only shown when REACT_APP_SHOW_ORG_SWITCHER=true (dev/testing) */}
            {organization && process.env.REACT_APP_SHOW_ORG_SWITCHER === 'true' && (
              <div className="organization-switcher" style={{ marginRight: '10px' }}>
                <OrganizationSwitcher
                  hidePersonal
                  afterSelectOrganizationUrl="/chat"
                  appearance={{
                    elements: {
                      organizationSwitcherTrigger: 'px-3 py-1.5 text-sm',
                      organizationPreview: 'px-2 py-1',
                      rootBox: 'text-white',
                      organizationSwitcherTriggerIcon: 'text-cyan-300',
                    },
                    variables: {
                      colorText: '#e0e7ff',
                      colorTextSecondary: '#a0aec0',
                      colorPrimary: '#00BCD4',
                    }
                  }}
                />
              </div>
            )}

            <div className="user-info">
              <span>
                {user.firstName || user.username || user.emailAddresses?.[0]?.emailAddress || 'User'}
              </span>
            </div>
            {/* Clerk's UserButton with dropdown menu */}
            <UserButton
              afterSignOutUrl="/"
              appearance={{
                elements: {
                  avatarBox: 'w-8 h-8'
                }
              }}
            />
          </>
        )}
      </div>
    </header>
  );
}

export default Header;
